import React from 'react';
import { useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'antd';
import { RichArea, Select, Input } from '_atoms';
import { SELECT_TYPES } from '_atoms/Select/constants';
import type { SelectOption } from '_atoms/Select';
import { Typography } from '_atoms/Typography';
import { ACTIONS, Can, UI } from 'permission';
import './styles.scss';

interface ServiceOption extends SelectOption {
  resource_manager?: unknown;
  service_head?: unknown;
  company?: unknown;
}

interface JobCreationFormProps {
  serviceOptions: ServiceOption[];
  teamLeadOptions: SelectOption[];
  recruiters: SelectOption[];
  recruiterLeads: SelectOption[];
  jobStatus?: string;
}

export const JobCreationForm: React.FC<JobCreationFormProps> = ({
  serviceOptions,
  teamLeadOptions,
  recruiters,
  recruiterLeads,
  jobStatus,
}) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const REQUIRED = { required: true, message: t('translation:job_creation_form.msg_required') };

  return (
    <Row>
      <Col flex="900px 0 0">
        <Row>
          <Col lg={12} md={12} sm={24}>
            <Typography.Title2>{t('translation:job_creation_form.title_general')}</Typography.Title2>
            <Can I={ACTIONS.UPDATE} a={UI.JOB_INFO_NAME} passThrough>
              {canEdit => (
                <Input
                  label={t('translation:job_creation_form.label_job_name')}
                  placeholder={t('translation:job_creation_form.ph_job_name')}
                  maxLength={100}
                  disabled={!canEdit}
                  itemProps={{ name: 'name', rules: [REQUIRED] }}
                />
              )}
            </Can>
            <Can I={ACTIONS.UPDATE} a={UI.JOB_SERVICE} passThrough>
              {canEdit => (
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.service !== curr.service}>
                  {({ setFields }) => (
                    <Select
                      labelInValue
                      label={t('translation:job_creation_form.label_service')}
                      placeholder={t('translation:job_creation_form.ph_service')}
                      options={serviceOptions}
                      disabled={!canEdit}
                      itemProps={{ name: 'service', rules: [REQUIRED] }}
                      onChange={({ resource_manager, service_head, company }: ServiceOption) =>
                        setFields([
                          { name: 'resource_manager', value: resource_manager },
                          { name: 'service_head', value: service_head },
                          { name: 'company', value: company },
                        ])
                      }
                    />
                  )}
                </Form.Item>
              )}
            </Can>
            <Select
              label={t('translation:job_creation_form.label_company')}
              placeholder={t('translation:job_creation_form.ph_company')}
              disabled
              itemProps={{ name: 'company' }}
            />
          </Col>
          <Can I={ACTIONS.UPDATE} a={UI.JOB_DESCRIPTION} passThrough>
            {canEdit => (
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.description_initial !== curr.description_initial}>
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item name="description" className="form-job-creation__form-item">
                    <RichArea
                      label={t('translation:job_creation_form.label_description')}
                      placeholder={t('translation:job_creation_form.ph_description')}
                      disabled={!canEdit}
                      initialValue={getFieldValue('description_initial')}
                      onChange={v => setFieldValue('description', v)}
                      maxLength={10000}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.JOB_BENEFITS} passThrough>
            {canEdit => (
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.benefits_initial !== curr.benefits_initial}>
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item name="benefits" className="form-job-creation__form-item">
                    <RichArea
                      label={t('translation:job_creation_form.label_benefits')}
                      placeholder={t('translation:job_creation_form.ph_benefits')}
                      disabled={!canEdit}
                      initialValue={getFieldValue('benefits_initial')}
                      onChange={v => setFieldValue('benefits', v)}
                      maxLength={10000}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
        </Row>
        <Row className="form-job-creation__team">
          <Col span={24}>
            <Typography.Title2>{t('translation:job_creation_form.title_team')}</Typography.Title2>
            <Row gutter={[32, 0]}>
              <Col lg={12} md={12} sm={24}>
                <Can I={ACTIONS.UPDATE} a={UI.JOB_TEAM_LEAD} passThrough>
                  {canEdit => (
                    <Select
                      labelInValue
                      label={t('translation:job_creation_form.label_team_lead')}
                      placeholder={t('translation:job_creation_form.ph_team_lead')}
                      options={teamLeadOptions}
                      disabled={!canEdit}
                      itemProps={{ name: 'team_lead', rules: [REQUIRED] }}
                    />
                  )}
                </Can>
              </Col>
              <Col lg={12} md={12} sm={24}>
                <Can I={ACTIONS.READ} this={subject(UI.UNASSIGN_RECRUITER_LEAD, { status: jobStatus })} passThrough>
                  {access => (
                    <Select
                      labelInValue
                      label={t('translation:job_creation_form.label_recruiter_leads')}
                      placeholder={t('translation:job_creation_form.ph_recruiter_leads')}
                      options={recruiterLeads}
                      disabled={!id || !access}
                      mode={SELECT_TYPES.MULTIPLE}
                      itemProps={{ name: 'recruiter_leads' }}
                    />
                  )}
                </Can>
              </Col>
            </Row>
            <Row gutter={[32, 0]}>
              <Col lg={12} md={12} sm={24}>
                <Select
                  labelInValue
                  label={t('translation:job_creation_form.label_resource_manager')}
                  placeholder={t('translation:job_creation_form.ph_resource_manager')}
                  disabled
                  itemProps={{ name: 'resource_manager' }}
                />
              </Col>
              <Col lg={12} md={12} sm={24}>
                <Can I={ACTIONS.UPDATE} this={subject(UI.JOB_RECRUITER, { status: jobStatus })} passThrough>
                  {canEdit => (
                    <Select
                      labelInValue
                      label={t('translation:job_creation_form.label_recruiters')}
                      placeholder={t('translation:job_creation_form.ph_recruiters')}
                      options={recruiters}
                      disabled={!canEdit}
                      mode={SELECT_TYPES.MULTIPLE}
                      itemProps={{ name: 'recruiters' }}
                    />
                  )}
                </Can>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[32, 0]}>
          <Col lg={12} md={12} sm={24}>
            <Select
              labelInValue
              label={t('translation:job_creation_form.label_service_head')}
              placeholder={t('translation:job_creation_form.ph_service_head')}
              disabled
              itemProps={{ name: 'service_head' }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
